import React, { useRef, useEffect, useState } from 'react';
import { useTheme, createUseStyles } from 'react-jss';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '@mdi/react';
import * as R from 'ramda';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { mdiClose, mdiArrowCollapseDown, mdiContentCopy } from '@mdi/js';

import QRCode from 'qrcode.react';
import Container from '@components/Container';
import Loading from '@components/Base/Loading';

import { wrapText, downloadFile, base64ToBlob } from '@utils/Draw';

import { unLockScroll } from '@utils/ui';
import { fetch } from '@utils/fetch';
import { getCustomData } from '@utils/Analysis';

const useStyles = createUseStyles(({
  fontColor,
  rem,
}) => ({
  '@global': {
    button: {
      outline: 'transparent',
      margin: rem(6),
    },
  },
  root: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    color: '#000',
    display: 'flex',
    alignItems: 'center',
    zIndex: 999,
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
  child: {
    position: 'relative',
    width: '100%',
    overflowY: 'auto',
  },
  content: {
    width: '90%',
    maxWidth: 480,
    borderRadius: 2,
    boxSizing: 'border-box',
    padding: rem(30),
    margin: [rem(60), 'auto', rem(20)],
    border: '1px solid rgba(13, 38, 91, 0.08)',
    boxShadow: '0px 1px 1px -1px rgba(13, 38, 91, 0.08), 0px 3px 3px 0px rgba(13, 38, 91, 0.06), 0px 5px 6px 2px rgba(13, 38, 91, 0.04)',
  },
  inner: {
    width: '100%',
    '& img': {
      width: '100%',
    }
  },
  title: {
    fontSize: rem(14),
    color: '#000000',
    textAlign: 'center',
    fontWeight: 'normal',
    lineHeight: '20px',
    marginBottom: 10,
  },
  close: {
    width: 24,
    height: 24,
    outline: 'transparent',
    border: 'unset',
    backgroundColor: 'transparent',
    color: fontColor.primary,
    position: 'absolute',
    top: 20,
    right: 20,
    cursor: 'pointer',
    padding: 0,
  },
  codeArea: {
    width: rem(100),
    '& > span': {
      color: '#111',
      fontSize: 10,
      marginTop: 5,
    }
  },
  save: {
    cursor: 'pointer',
    height: 24,
    margin: [rem(20), 'auto'],
    '& span':{
      fontSize: rem(16),
      marginLeft: 10,
      lineHeight: '20px',
    }
  },
  copy: {
    width: '90%',
    margin: [rem(20), 'auto'],
    paddingTop: 20,
    borderTop: '1px solid #E4E4E4',
    '& p':{
      fontSize: rem(16),
      marginLeft: 10,
      lineHeight: '20px',
    },
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(0, 0, 0, 0.55)',
    fontSize: rem(16),
    marginLeft: 10,
    padding: 5,
    lineHeight: '25px',
    borderRadius: 2,
    boxShadow: '0px 1px 1px -1px rgba(13, 38, 91, 0.08), 0px 3px 3px 0px rgba(13, 38, 91, 0.06), 0px 5px 6px 2px rgba(13, 38, 91, 0.04)',
    '& > span': {
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      wordBreak: 'break-all',
    }
  },
  logo: {
    width: `${rem(75)} !important`,
    height: rem(15),
  },
  copyIcon: {
    height: 40,
    width: 60,
    margin: '0 10px',
  },
  toast: {
    position: 'absolute',
    padding: [rem(10), 0],
    fontSize: rem(18),
    textAlign: 'center',
    width: rem(200),
    bottom: rem(30),
    marginLeft: '50%',
    left: `-${rem(100)}`,
    borderRadius: 10,
    color: 'white',
    backgroundColor: 'rgba(0, 128, 0, .5)',
  },
  picMask: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    zIndex: 9999,
    color: '#fff',
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, .9)',
  },
}), {
    name: 'ShareModal'
});

const ShareModal = props => {
  const { t, setOpenShare, language } = props;
  const theme = useTheme();
  const classes = useStyles(theme);
  const rootRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [isDone, setIsDone] = useState(false);
  const [isWx, setIsWx] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [imgSrc, setImgSrc] = useState(undefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = () => {
    setLoading(true);
    const params = {
      types: [
        'sku', 
        'customizedDisplayInfo',
      ],
      language,
    };
    fetch(params).then((data) => {
      setData(data);
      setLoading(false);
    }).catch((err) => {
      console.log(err);
    });
  }

  const handleCheckEnv = () => {
    var ua = global.window.navigator.userAgent.toLowerCase();
    // eslint-disable-next-line eqeqeq
    const wxEnv = ua.match(/MicroMessenger/i) == 'micromessenger';
    const wxWebView = global.window.__wxjs_environment;
    // 支持微信打开或者小程序嵌入的webview打开
    if (wxEnv || (wxWebView && wxWebView === 'miniprogram')) {
      setIsWx(true);
    }
    // 判断是否为安卓
    if (ua && (/(Android)/i.test(ua))) {
      setIsAndroid(true);
    }
  }

  useEffect(() => {
    handleCheckEnv();
    fetchData();
  }, [])

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    }
  }, [success])

  const skuData = R.pathOr({}, ['jsonData', 'sku'])(data);
  const base = R.pathOr({}, ['base'])(skuData);
  const productImage = R.pathOr('', ['mainimage', 'filelocation'])(base);

  const custom = getCustomData(data);
  const logoImage = R.pathOr('', ['logoimage', 'filelocation'])(custom);

  const cretateBlob = (content) => {
    const blob = base64ToBlob(content);
    return URL.createObjectURL(blob);
  }

  const handleSavePng = () => {
    const canvas = global.document.createElement('canvas');
    canvas.width = 380;
    canvas.height = 440;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#FFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const img = new global.Image();
    img.crossOrigin = 'Anonymous';
    img.src = productImage;

    const logo = new global.Image();
    logo.crossOrigin = 'Anonymous';
    logo.src = logoImage;

    const p = new Promise((resolve) => {
      img.onload = () => resolve();
    });

    p.then(() => {
      // img
      ctx.drawImage(img, 50, 50, 300, 300);

      // 标题
      const title = t('tshirt');
      ctx.font = 'bold 16px sans-serif';
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      wrapText(ctx, title, 120, 360);

      ctx.drawImage(logo, 50, 370, 130, 30);

      // qrCode
      const qrCanvas = global.document.querySelector('.HpQrcode > canvas');
      ctx.drawImage(qrCanvas, 265, 320, 80, 80);

      // 扫码看一看
      const content = t('scan');
      ctx.font = 'bold 12px sans-serif';
      ctx.fillStyle = '#000000';
      wrapText(ctx, content, 305, 420);
    }).then(() => {
      const dataUrl = canvas.toDataURL();
      if (isWx) {
        if (isAndroid) {
          // 安卓将直接base64编码
          setImgSrc(dataUrl);
        } else {
          const imgUri = cretateBlob(dataUrl);
          setImgSrc(imgUri);
        } 
        setIsDone(true);
      } else {
        downloadFile('poster.png', dataUrl);
      }
    });
  }

  const createToast = () => {
    if (!success) return null;
    if (success) {
      return (
        <span className={classes.toast}>
          {t('copy_success')}
        </span>
      )
    }
  }

  const handleCopy = () => {
    const str = global.window.location.href;
    if (!str) return false;
    setSuccess(true);
    return false;
  }

  const handleCloseShare = () => {
    setOpenShare(false);
    unLockScroll();
  }

  const createMain = () => (
    <Container className={classes.child}>
      <button
        onClick={handleCloseShare}
        className={classNames(classes.close)} 
      >
        <Icon path={mdiClose} size='24px' />
      </button>

      <Container 
        flex 
        className={classNames(classes.content)}
      >
        <Container 
          flex
          justify='center'
          alignItems='center'
          direction='column'
          className={classNames(classes.inner)}
        >
          <img 
            src={productImage}
            alt='product'
            width={300}
          />
          <Container
            flex
            justify='space-between'
            alignItems='center'
            style={{ width: '100%' }}
          >
            <Container flex direction='column' alignItems='center'>
              <span className={classes.title}>{base.productname || t('noData')}</span>
              <img 
                src={logoImage}
                alt='logo'
                width={100}
                height={30}
                className={classes.logo}
              />
            </Container>

            <Container 
              flex
              direction='column'
              alignItems='center'
              className={classNames(classes.codeArea)}
            >
              <QRCode
                value={global.window.location.href}
                size={60}
                level='L'
                bgColor='#ffffff'
                id='canvas'
              />
              <span>{t('scan')}</span>
            </Container>
          </Container>
        
        </Container>
      </Container>

      <Container 
        flex 
        alignItems='center'
        justify='center'
        className={classes.save}
        onClick={handleSavePng}
      >
        <Icon path={mdiArrowCollapseDown} size='20px' />
        <span>{t('save2Share')}</span>
      </Container>

      <Container 
        flex 
        direction='column'
        className={classes.copy}
      >
        <p>{t('copyLink')}</p>
        <p className={classes.link}>
          <span>{global.window.location.href}</span>
          <CopyToClipboard 
            text={global.window.location.href}
            onCopy={handleCopy}
          >
            <Icon
              path={mdiContentCopy} 
              className={classes.copyIcon}
            />
          </CopyToClipboard>
        </p>
      </Container>

      <div style={{ display: 'none' }} className='HpQrcode'>
        <QRCode
          value={global.window.location.href}
          size={80}
          level='L'
          bgColor='#ffffff'
          id='canvas'
        />
      </div>

      {
        success && createToast()
      }
    </Container>
  );

  return (
    <Container
      flex
      direction='column'
      items='space-between'
      ref={rootRef}
      className={classNames(classes.root)}
    >
      {loading ? <Loading size={2} /> : createMain()}
      {
        (isWx && isDone) && (
          <div className={classes.picMask}>
            <span style={{ fontSize: 16, marginBottom: 10 }}>提醒: 请长按图片进行保存</span>
            <Icon 
              path={mdiClose} 
              size='24px' 
              className={classes.close} 
              onClick={() => setIsDone(false)}
            />
            <img src={imgSrc} alt="icon" width={300} />
          </div>
        )
      }
    </Container>
  )
};

ShareModal.propTypes = {
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  setOpenShare: PropTypes.func.isRequired,
}

export default ShareModal;